<template>
    <div>
        <FadeTransition mode="out-in">
            <div v-if="!isSubmitted">
                <p class="text-muted-foreground mb-10">
                    Please sign in with your government email to continue
                </p>
                <form @submit="reqOtp">
                    <fieldset class="grid gap-6" :disabled="isSubmitting">
                        <TextControlField
                            name="email"
                            label="Government Email"
                            placeholder="e.g. youremail@mtic.gov.bn">
                        </TextControlField>
                        <!-- <TextControlField name="password" label="Password" type="password" placeholder="Password">
                        </TextControlField> -->

                        <div class="relative grid">
                            <Button
                                variant="yellow"
                                size="lg"
                                :class="[isSubmitting && 'text-transparent']">
                                Request OTP
                            </Button>
                            <div
                                :class="[
                                    'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                                    isSubmitting ? 'opacity-100' : 'opacity-0',
                                ]">
                                <Spinner class="text-primary-foreground"></Spinner>
                            </div>
                        </div>
                    </fieldset>
                </form>
                <div v-if="isForbidden" class="mt-4 text-center">
                    <p class="text-muted-foreground text-xs">
                        <!-- <slot name="login-forbidden-error"></slot> -->
                        We're sorry, but your email address is not currently authorized to access
                        GoBN. This platform is currently being rolled out in phases to registered
                        government agencies and authorized users.
                    </p>
                </div>
            </div>
            <div v-else-if="isFirstLogin">
                <div class="mb-10">
                    <slot name="workplace-message-content"></slot>
                </div>
                <div class="grid gap-6">
                    <AutocompleteControlField
                        name="tenant"
                        id="Ministry"
                        :items="tenantsList"
                        item-text="name"
                        item-value="key"
                        placeholder="Please select your workplace Ministry"
                        label="Ministry"
                        :disabled="isSubmitting">
                    </AutocompleteControlField>
                    <AutocompleteControlField
                        name="subtenant"
                        id="Department"
                        :items="subTenants"
                        :disabled="!values.tenant || isSubmitting"
                        item-text="name"
                        item-value="key"
                        label="Ministry Department"
                        placeholder="Please select your department">
                    </AutocompleteControlField>
                    <div class="relative grid">
                        <Button
                            @click="assignWorkplace"
                            variant="yellow"
                            size="lg"
                            :class="[isSubmitting && 'text-transparent']"
                            :disabled="isSubmitting">
                            Submit
                        </Button>
                        <div
                            :class="[
                                'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                                isSubmitting ? 'opacity-100' : 'opacity-0',
                            ]">
                            <Spinner class="text-primary-foreground"></Spinner>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else-if="isSubmitted">
                <!-- <div v-if="!isVerified" class="text-center">
                    <p class="text-muted-foreground">
                        We've sent you an email to {{ newEmailAddress }} containing a link to verify
                        your account. Click on it to continue
                    </p>
                </div> -->
                <p class="text-muted-foreground mb-10">
                    We've sent an email to {{ userEmail }} containing the OTP to verify your
                    account.
                </p>
                <form @submit="verifyOtp">
                    <fieldset class="grid gap-6" :disabled="isSubmitting">
                        <div>
                            <Label for="otp"> OTP </Label>
                            <PinInput name="otp" id="otp" v-model="value">
                                <PinInputGroup class="w-full justify-between">
                                    <PinInputSlot
                                        v-for="(id, index) in 6"
                                        class="text-muted-foreground !size-14"
                                        :key="id"
                                        :index="index" />
                                </PinInputGroup>
                            </PinInput>
                            <p v-if="errorMessage" class="error-message text-destructive text-sm">
                                {{ errorMessage }}
                            </p>
                        </div>
                        <div class="flex items-center gap-1 text-sm">
                            <p class="text-muted-foreground">Didn't receive the OTP?</p>
                            <Button
                                v-if="canResend"
                                @click="resendOtp"
                                type="button"
                                variant="link"
                                :class="[isSubmitting && 'text-transparent']"
                                :disabled="sendingOtp">
                                Resend
                            </Button>
                            <p v-else class="text-muted-foreground">
                                Resend OTP in
                                <NumberFlow :value="countdown" suffix="s" />
                            </p>
                        </div>
                        <div class="relative grid">
                            <Button
                                variant="yellow"
                                size="lg"
                                :class="[isSubmitting && 'text-transparent']">
                                Verify OTP
                            </Button>
                            <div
                                :class="[
                                    'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                                    isSubmitting ? 'opacity-100' : 'opacity-0',
                                ]">
                                <Spinner class="text-primary-foreground"></Spinner>
                            </div>
                        </div>
                    </fieldset>
                </form>
            </div>
        </FadeTransition>
    </div>
</template>

<script lang="ts" setup>
import { TextControlField } from '@/website/components/core/forms/text-control'
import { Button } from '@/components/ui/button'
import { useField, useForm } from 'vee-validate'
import { computed, ref, watch } from 'vue'
import { array, object, string } from 'yup'
import { Spinner } from '@/components/core/spinners'
import { usersApi } from '@/website/api/users'
import { FadeTransition } from '@/components/core/transitions'
import { PinInput, PinInputGroup, PinInputSlot } from '@/components/ui/pin-input'
import { Label } from '@/components/ui/label'
import { useInterval } from '@vueuse/core'
import { AutocompleteControlField } from '@/components/core/forms/autocomplete-control'
import NumberFlow from '@number-flow/vue'
import type { UserTenants } from '@/apps/website/types'

interface Props {
    confirmationMessage: string
}

defineProps<Props>()

interface FormFields {
    otp: string[]
    email: string
    tenant: string
    subtenant: string
}

// const isVerified = ref(false)
const isFirstLogin = ref(false)
const isSubmitted = ref(false)
const isForbidden = ref(false)
const userEmail = ref('')
const countdown = ref(0)
const canResend = computed(() => countdown.value === 0)
const sendingOtp = ref(false)

// const newEmailAddress = ref('')
const redirectUrl = ref('')
const tenantsList = ref<UserTenants[]>([])
const subTenants = ref<UserTenants[]>([])

const validationSchema = computed(() => {
    if (!isSubmitted.value) {
        // Initial state: only validate email
        return object({
            email: string().required().email().label('Government Email'),
        })
    } else if (isFirstLogin.value) {
        return object({
            tenant: string().required().label('Ministry'),
            subtenant: string().required().label('Department'),
        })
    } else {
        // After OTP requested: only validate OTP
        return object({
            otp: array()
                .of(string().required())
                .min(6, 'OTP must be 6 digits')
                .max(6, 'OTP must be 6 digits')
                .test('all-filled', 'All OTP fields must be filled', (value) => {
                    return (
                        value && value.length === 6 && value.every((digit) => digit.trim() !== '')
                    )
                })
                .label('OTP'),
        })
    }
})

const { handleSubmit, isSubmitting, values, setFieldValue } = useForm<FormFields>({
    validationSchema,
})

const { errorMessage, value } = useField<string[]>('otp')

const { pause, resume } = useInterval(1000, {
    callback: () => {
        countdown.value--
        if (countdown.value === 0) {
            pause()
        }
    },
    controls: true,
    immediate: false,
})

const startCountdown = () => {
    countdown.value = 120
    resume()
}

const reqOtp = handleSubmit(async (values) => {
    const [result, error] = await usersApi.requestOtp(values.email)

    if (error === 'AUTH_FORBIDDEN') {
        isForbidden.value = true
        return
    }

    if (!error && result) {
        userEmail.value = values.email
        isSubmitted.value = true
        startCountdown()
    }
})

const resendOtp = async () => {
    if (!canResend.value) return

    sendingOtp.value = true
    const [result, error] = await usersApi.requestOtp(userEmail.value)

    if (!error && result) {
        sendingOtp.value = false
        startCountdown()
    }
}

const verifyOtp = handleSubmit(async () => {
    const url = new URL(window.location.href)
    const returnPath = url.searchParams.get('returnPath') || undefined

    const [result, error] = await usersApi.login(userEmail.value, values.otp.join(''))

    if (!result || error) {
        isSubmitted.value = false

        return
    }

    //     if (typeof result === 'string') {
    //         isVerified.value = false
    //         isSubmitted.value = true
    //         newEmailAddress.value = result
    //         return
    //     }

    //     let redirectUrl = result.redirectUrl
    //     if (returnPath && returnPath.startsWith('/')) {
    //         redirectUrl += `#${returnPath}`
    //     }

    //     window.location.assign(redirectUrl)

    // then do the redirection
    if (result && typeof result !== 'string' && !error) {
        isFirstLogin.value = result.isFirstTimeLogin

        redirectUrl.value = result.redirectUrl
        if (returnPath && returnPath.startsWith('/')) {
            redirectUrl.value += `#${returnPath}`
        }

        // redirect if not first time login
        if (!isFirstLogin.value) {
            window.location.assign(redirectUrl.value)
        }
    }
})

const loadTenants = async () => {
    const [result, error] = await usersApi.getTenantsList()

    if (result && !error) {
        tenantsList.value = result
    }
}

const loadSubTenants = async (ministryName: string) => {
    const [result, error] = await usersApi.getSubTenantsList(ministryName)

    if (result && !error) {
        subTenants.value = result
    }
}

const assignWorkplace = handleSubmit(async () => {
    const [result, error] = await usersApi.assignTenant(
        userEmail.value,
        values.tenant,
        values.subtenant,
    )

    if (result && !error) {
        window.location.assign(redirectUrl.value)
    }
})

watch(
    () => isFirstLogin.value,
    () => {
        if (isFirstLogin.value) {
            loadTenants()
        }
    },
)

loadTenants()

// Reset department field when ministry changes
watch(
    () => values.tenant,
    () => {
        loadSubTenants(values.tenant)

        setFieldValue('subtenant', '')
    },
)
</script>

<style scoped>
.v-enter-active,
.v-leave-active {
    transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
}
</style>
